import { Button } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { Form } from '@/api';
import { FormAnalytics } from '@/components/FormMessages/FormAnalytics';
import { FormMessagesList } from '@/components/FormMessages/FormMessagesList';
import { FormRejections } from '@/components/FormMessages/FormRejections';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { parseFormSpecToml } from '@/lib/form-specs';
import { useRustyState } from '@/state';

export function FormMessagesScreen() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [form, setForm] = useState<Form | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    let live = true;
    useRustyState
      .getState()
      .api.getForm(id)
      .then((res) => live && setForm(res as Form))
      .catch(() => live && setForm(null));
    return () => {
      live = false;
    };
  }, [id]);

  const spec = useMemo(() => {
    if (!form?.specs?.trim()) return null;
    const parsed = parseFormSpecToml(form.specs);
    return parsed.ok && Object.keys(parsed.spec.fields).length > 0 ? parsed.spec : null;
  }, [form]);

  if (!id || form === null) {
    return (
      <Page>
        <PageHeader
          title={t('formMessages.pageTitle')}
          breadcrumb={[{ label: t('forms.pageTitle'), to: '/account/forms' }]}
        />
        <EmptyText>{t('formMessages.notFound')}</EmptyText>
      </Page>
    );
  }

  const title = form?.title ?? '';

  return (
    <Page>
      <PageHeader
        title={<span>{form ? t('formMessages.titleFor', { title }) : '…'}</span>}
        documentTitle={form ? t('formMessages.titleFor', { title }) : t('formMessages.pageTitle')}
        breadcrumb={[
          { label: t('forms.pageTitle'), to: '/account/forms' },
          { label: title || '…', to: `/account/forms/${id}` },
          // The last crumb renders as the current page, not a link.
          { label: t('formMessages.pageTitle'), to: `/account/forms/${id}/messages` },
        ]}
        actions={
          <Button component={Link} to={createLanguageURL(`/account/forms/${id}`)} variant="default">
            {t('formMessages.formSettings')}
          </Button>
        }
      />
      <FormAnalytics formId={id} />
      {form && <FormRejections formId={id} spec={spec} />}
      {form && (
        <FormMessagesList
          formId={id}
          formTitle={title}
          encrypted={form.encryption_mode === 'nostr'}
          retentionDays={form.retention_days}
          filterSpam={form.filter_spam}
        />
      )}
    </Page>
  );
}
